/**
 * LEOS Mission Ready - Ground Station Network Wizard
 * Intelligent network configuration wizard for optimal station placement
 */

import { COVERAGE_ZONES } from './ground-station-presets.js';
import { apiClient } from './api-integration.js';

export class GroundStationWizard {
    constructor(parentUI) {
        this.parentUI = parentUI;
        this.currentStep = 1;
        this.wizardData = {
            missionProfile: null,
            coverage: 'global',
            stationCount: 5,
            minElevation: 10,
            capabilities: ['s-band']
        };
        this.generatedStations = [];
    }
    
    getWizardHTML() {
        return `
            <div class="wizard-step" id="wizard-step-1">
                <h4>Step 1: Mission Profile</h4>
                <div class="mission-profiles">
                    <label class="profile-option">
                        <input type="radio" name="mission-profile" value="leo-constellation">
                        <div class="profile-card">
                            <h5>LEO Constellation</h5>
                            <p>Track large constellations of low Earth orbit satellites</p>
                        </div>
                    </label>
                    <label class="profile-option">
                        <input type="radio" name="mission-profile" value="deep-space">
                        <div class="profile-card">
                            <h5>Deep Space Mission</h5>
                            <p>Support interplanetary missions and lunar operations</p>
                        </div>
                    </label>
                    <label class="profile-option">
                        <input type="radio" name="mission-profile" value="earth-observation">
                        <div class="profile-card">
                            <h5>Earth Observation</h5>
                            <p>Maximum coverage for remote sensing satellites</p>
                        </div>
                    </label>
                    <label class="profile-option">
                        <input type="radio" name="mission-profile" value="launch-operations">
                        <div class="profile-card">
                            <h5>Launch Operations</h5>
                            <p>Support rocket launches and early orbit phases</p>
                        </div>
                    </label>
                </div>
            </div>
            
            <div class="wizard-step" id="wizard-step-2" style="display: none;">
                <h4>Step 2: Coverage Requirements</h4>
                <div class="form-group">
                    <label>Primary Coverage Region</label>
                    <select id="wizard-coverage-region">
                        <option value="global">Global Coverage</option>
                        <option value="north-america">North America</option>
                        <option value="europe">Europe</option>
                        <option value="asia-pacific">Asia Pacific</option>
                        <option value="polar">Polar Regions</option>
                        <option value="equatorial">Equatorial Belt</option>
                    </select>
                </div>
                <div class="form-row">
                    <div class="form-group">
                        <label>Number of Stations</label>
                        <input type="number" id="wizard-station-count" value="5" min="1" max="50">
                    </div>
                    <div class="form-group">
                        <label>Minimum Elevation Angle (°)</label>
                        <input type="number" id="wizard-min-elevation" value="10" min="5" max="45">
                    </div>
                </div>
                <div class="form-group">
                    <label>Station Capabilities</label>
                    <div class="capability-checkboxes">
                        <label><input type="checkbox" value="s-band" checked> S-Band (2-4 GHz)</label>
                        <label><input type="checkbox" value="x-band"> X-Band (8-12 GHz)</label>
                        <label><input type="checkbox" value="ka-band"> Ka-Band (26-40 GHz)</label>
                        <label><input type="checkbox" value="optical"> Optical Communication</label>
                    </div>
                </div>
            </div>
            
            <div class="wizard-step" id="wizard-step-3" style="display: none;">
                <h4>Step 3: Review & Generate</h4>
                <div class="wizard-summary">
                    <div class="summary-item">
                        <strong>Mission Type:</strong> <span id="wizard-summary-mission"></span>
                    </div>
                    <div class="summary-item">
                        <strong>Coverage:</strong> <span id="wizard-summary-coverage"></span>
                    </div>
                    <div class="summary-item">
                        <strong>Stations:</strong> <span id="wizard-summary-stations"></span>
                    </div>
                    <div class="summary-item">
                        <strong>Capabilities:</strong> <span id="wizard-summary-capabilities"></span>
                    </div>
                </div>
                <div class="wizard-preview" id="wizard-network-preview">
                    <!-- Network preview will be inserted here -->
                </div>
            </div>
            
            <div class="wizard-controls">
                <button class="wizard-btn" id="wizard-prev" style="display: none;">Previous</button>
                <button class="wizard-btn primary" id="wizard-next">Next</button>
                <button class="wizard-btn success" id="wizard-generate" style="display: none;">Generate Network</button>
            </div>
        `;
    }
    
    attachEventHandlers(modal) {
        const wizardNext = modal.querySelector('#wizard-next');
        const wizardPrev = modal.querySelector('#wizard-prev');
        const wizardGenerate = modal.querySelector('#wizard-generate');
        
        wizardNext.addEventListener('click', () => this.nextStep(modal));
        wizardPrev.addEventListener('click', () => this.prevStep(modal));
        wizardGenerate.addEventListener('click', () => this.generateNetwork(modal));
    }
    
    nextStep(modal) {
        if (this.currentStep < 3) {
            // Validate current step
            if (this.currentStep === 1 && !modal.querySelector('input[name="mission-profile"]:checked')) {
                this.parentUI.showStatus('Please select a mission profile', 'error');
                return;
            }
            
            // Save current step data
            this.saveStepData(modal);
            
            // Hide current step
            modal.querySelector(`#wizard-step-${this.currentStep}`).style.display = 'none';
            
            // Show next step
            this.currentStep++;
            modal.querySelector(`#wizard-step-${this.currentStep}`).style.display = 'block';
            
            // Update buttons
            modal.querySelector('#wizard-prev').style.display = 'block';
            
            if (this.currentStep === 3) {
                modal.querySelector('#wizard-next').style.display = 'none';
                modal.querySelector('#wizard-generate').style.display = 'block';
                this.updateSummary(modal);
            }
        }
    }
    
    prevStep(modal) {
        if (this.currentStep > 1) {
            // Hide current step
            modal.querySelector(`#wizard-step-${this.currentStep}`).style.display = 'none';
            
            // Show previous step
            this.currentStep--;
            modal.querySelector(`#wizard-step-${this.currentStep}`).style.display = 'block';
            
            // Update buttons
            if (this.currentStep === 1) {
                modal.querySelector('#wizard-prev').style.display = 'none';
            }
            
            if (this.currentStep === 2) {
                modal.querySelector('#wizard-next').style.display = 'block';
                modal.querySelector('#wizard-generate').style.display = 'none';
            }
        }
    }
    
    saveStepData(modal) {
        switch (this.currentStep) {
            case 1:
                this.wizardData.missionProfile = modal.querySelector('input[name="mission-profile"]:checked')?.value;
                break;
            case 2:
                this.wizardData.coverage = modal.querySelector('#wizard-coverage-region').value;
                this.wizardData.stationCount = parseInt(modal.querySelector('#wizard-station-count').value);
                this.wizardData.minElevation = parseFloat(modal.querySelector('#wizard-min-elevation').value);
                this.wizardData.capabilities = Array.from(modal.querySelectorAll('.capability-checkboxes input:checked'))
                    .map(cb => cb.value);
                break;
        }
    }
    
    updateSummary(modal) {
        modal.querySelector('#wizard-summary-mission').textContent = 
            this.wizardData.missionProfile.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        modal.querySelector('#wizard-summary-coverage').textContent = 
            this.wizardData.coverage.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        modal.querySelector('#wizard-summary-stations').textContent = `${this.wizardData.stationCount} stations`;
        modal.querySelector('#wizard-summary-capabilities').textContent = 
            this.wizardData.capabilities.map(c => c.toUpperCase().replace('-', ' ')).join(', ') || 'S-Band';
    }
    
    async generateNetwork(modal) {
        this.parentUI.showStatus('Generating optimal network configuration...', 'loading');
        
        try {
            this.generatedStations = this.generateOptimalNetwork();
            
            // Show preview
            const preview = modal.querySelector('#wizard-network-preview');
            preview.innerHTML = `
                <h5 style="color: #e2e8f0; margin: 0 0 16px 0;">Generated Network (${this.generatedStations.length} stations)</h5>
                <div style="display: grid; gap: 8px; max-height: 300px; overflow-y: auto;">
                    ${this.generatedStations.map((station, i) => `
                        <div style="background: rgba(0, 153, 255, 0.1); border: 1px solid rgba(0, 153, 255, 0.3); padding: 8px; border-radius: 6px;">
                            <strong style="color: #00d4ff;">${station.name}</strong>
                            <span style="color: #94a3b8; margin-left: 8px;">
                                ${station.latitude.toFixed(2)}°, ${station.longitude.toFixed(2)}° · ${station.type}
                            </span>
                        </div>
                    `).join('')}
                </div>
                <div style="margin-top: 16px; text-align: center;">
                    <button class="submit-btn" onclick="groundStationUploadUI.wizardModule.deployGeneratedNetwork()">
                        Deploy Network
                    </button>
                </div>
            `;
            
            this.parentUI.showStatus(`Generated ${this.generatedStations.length} optimal station locations`, 'success');
        } catch (error) {
            this.parentUI.showStatus(`Error: ${error.message}`, 'error');
        }
    }
    
    generateOptimalNetwork() {
        const stations = [];
        const zone = COVERAGE_ZONES[this.wizardData.coverage] || COVERAGE_ZONES.global;
        
        // Advanced placement algorithms based on mission type
        if (this.wizardData.missionProfile === 'deep-space') {
            // Deep space requires 3 stations at 120° intervals for continuous coverage
            const longitudes = this.calculateOptimalLongitudes(3, zone);
            for (let i = 0; i < Math.min(3, this.wizardData.stationCount); i++) {
                stations.push(this.createStation(i, 'dsn', zone.minLat + 35, longitudes[i], 70, 15000));
            }
            // Add additional tracking stations if needed
            for (let i = 3; i < this.wizardData.stationCount; i++) {
                const lat = this.calculateOptimalLatitude(i, zone, this.wizardData.stationCount);
                const lon = this.calculateOptimalLongitude(i, zone, this.wizardData.stationCount);
                stations.push(this.createStation(i, 'tracking', lat, lon, 34, 5000));
            }
        } else if (this.wizardData.missionProfile === 'leo-constellation') {
            // LEO constellations need evenly distributed stations
            for (let i = 0; i < this.wizardData.stationCount; i++) {
                const lat = this.calculateOptimalLatitude(i, zone, this.wizardData.stationCount);
                const lon = this.calculateOptimalLongitude(i, zone, this.wizardData.stationCount);
                stations.push(this.createStation(i, 'tracking', lat, lon, 11, 3000));
            }
        } else if (this.wizardData.missionProfile === 'earth-observation') {
            // Earth observation benefits from polar and equatorial stations
            const polarCount = Math.floor(this.wizardData.stationCount * 0.3);
            const equatorialCount = this.wizardData.stationCount - polarCount;
            
            // Add polar stations
            for (let i = 0; i < polarCount; i++) {
                const lat = 65 + (i % 2) * 10; // Alternate between 65° and 75°
                const lon = this.calculateOptimalLongitude(i, zone, polarCount);
                stations.push(this.createStation(i, 'observatory', lat * (i % 2 ? -1 : 1), lon, 20, 4000));
            }
            
            // Add equatorial stations
            for (let i = 0; i < equatorialCount; i++) {
                const lat = -20 + (40 * i / equatorialCount);
                const lon = this.calculateOptimalLongitude(i, zone, equatorialCount);
                stations.push(this.createStation(polarCount + i, 'observatory', lat, lon, 15, 3500));
            }
        } else if (this.wizardData.missionProfile === 'launch-operations') {
            // Launch operations need stations near launch sites and downrange
            for (let i = 0; i < this.wizardData.stationCount; i++) {
                const isLaunchSite = i % 3 === 0;
                const lat = this.calculateOptimalLatitude(i, zone, this.wizardData.stationCount);
                const lon = this.calculateOptimalLongitude(i, zone, this.wizardData.stationCount);
                stations.push(this.createStation(i, isLaunchSite ? 'launch' : 'tracking', lat, lon, 
                    isLaunchSite ? 25 : 15, isLaunchSite ? 4000 : 3000));
            }
        }
        
        return stations;
    }
    
    createStation(index, type, lat, lon, diameter, range) {
        return {
            name: `${this.wizardData.coverage.toUpperCase()}-${this.wizardData.missionProfile.toUpperCase()}-${index + 1}`,
            latitude: lat,
            longitude: lon,
            altitude: Math.random() * 500, // Random elevation 0-500m
            type: type,
            frequency: this.getFrequencyForCapabilities(),
            diameter: diameter,
            range: range,
            description: `Auto-generated station for ${this.wizardData.missionProfile} mission`,
            capabilities: this.wizardData.capabilities
        };
    }
    
    calculateOptimalLatitude(index, zone, total) {
        return zone.minLat + (zone.maxLat - zone.minLat) * ((index + 0.5) / total);
    }
    
    calculateOptimalLongitude(index, zone, total) {
        const baseSpacing = (zone.maxLon - zone.minLon) / total;
        const offset = (index % 2) * baseSpacing * 0.5; // Stagger alternate stations
        return zone.minLon + (baseSpacing * index + offset) % (zone.maxLon - zone.minLon);
    }
    
    calculateOptimalLongitudes(count, zone) {
        const longitudes = [];
        const spacing = 360 / count;
        const start = zone.minLon + (zone.maxLon - zone.minLon) / 2;
        
        for (let i = 0; i < count; i++) {
            let lon = start + (i * spacing);
            // Wrap around if necessary
            if (lon > 180) lon -= 360;
            if (lon < -180) lon += 360;
            longitudes.push(lon);
        }
        
        return longitudes;
    }
    
    getFrequencyForCapabilities() {
        if (this.wizardData.capabilities.includes('ka-band')) return 32.0;
        if (this.wizardData.capabilities.includes('x-band')) return 8.4;
        if (this.wizardData.capabilities.includes('s-band')) return 2.3;
        return 2.3; // Default
    }
    
    async deployGeneratedNetwork() {
        if (!this.generatedStations || this.generatedStations.length === 0) {
            this.parentUI.showStatus('No generated network to deploy', 'error');
            return;
        }
        
        this.parentUI.showStatus(`Deploying ${this.generatedStations.length} ground stations...`, 'loading');
        
        try {
            for (const station of this.generatedStations) {
                await apiClient.createGroundStation(station);
            }
            
            this.parentUI.showStatus(`Successfully deployed ${this.generatedStations.length} ground stations!`, 'success');
            this.parentUI.loadCurrentStations();
            
            // Reload scene after delay
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (error) {
            this.parentUI.showStatus(`Error deploying network: ${error.message}`, 'error');
        }
    }
    
    reset() {
        this.currentStep = 1;
        this.wizardData = {
            missionProfile: null,
            coverage: 'global',
            stationCount: 5,
            minElevation: 10,
            capabilities: ['s-band']
        };
        this.generatedStations = [];
    }
}